import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 20px', fontFamily: 'system-ui, -apple-system, sans-serif', lineHeight: '1.6' }}>
      <h1 style={{ borderBottom: '2px solid #eee', paddingBottom: '10px' }}>Privacy Policy</h1>
      <p><strong>Streamers Connected Media Ltd</strong></p>
      <p><em>Last updated: 15 August 2026</em></p>

      <hr style={{ margin: '30px 0', border: 'none', borderTop: '1px solid #eee' }} />

      <h2>1. Introduction</h2>
      <p>
        Streamers Connected Media Ltd ("we", "us", or "our") operates the Streamers Connected analytics platform. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service.
      </p>
      <p>
        <strong>This platform uses YouTube API Services, TikTok API Services, and the Instagram Graph API provided by Meta Platforms.</strong> By using our Service, you are also agreeing to be bound by the{' '}
        <a href="http://www.google.com/policies/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a>, the{' '}
        <a href="https://www.tiktok.com/legal/page/global/privacy-policy/en" target="_blank" rel="noopener noreferrer">TikTok Privacy Policy</a>, and the{' '}
        <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer">Meta Privacy Policy</a>.
      </p>

      <h2>2. Information We Collect</h2>

      <h3>Information You Provide</h3>
      <p>When you are set up as a client on our platform, we may collect:</p>
      <ul>
        <li>Your name</li>
        <li>Email address</li>
        <li>Company/organisation name</li>
        <li>Business contact information</li>
      </ul>

      <h3>Information Collected Automatically</h3>
      <p>When you access our dashboards, we automatically collect:</p>
      <ul>
        <li>Browser type and version</li>
        <li>IP address</li>
        <li>Pages visited and time spent</li>
        <li>Access times and dates</li>
      </ul>

      <h3>Information from Third-Party Platforms</h3>
      <p>Our platform displays publicly available data from YouTube, Twitch, Kick, TikTok, and Instagram, including:</p>
      <ul>
        <li>Creator channel names and profile information</li>
        <li>Video/stream titles and descriptions</li>
        <li>Public metrics such as views, likes, comments, and subscriber counts</li>
        <li>Publicly available engagement data</li>
      </ul>
      <p>Instagram data is retrieved via the Instagram Graph API's Business Discovery feature, which returns publicly available information from Instagram Business and Creator accounts, such as post and reel captions, like and comment counts, and video view counts.</p>
      <p>Kick data is retrieved via Kick's official public developer API using application credentials, without any creator login. It returns publicly available information about Kick channels and live streams, such as channel names, profile images, channel descriptions, stream titles, stream categories, live viewer counts, and active subscriber counts. Kick's API does not provide past-broadcast data; where a Kick stream's replay view count is shown, it has been entered by our team from the publicly visible figure on Kick. We only link a Kick channel to a creator profile where the creator has supplied the link or where the channel is publicly cross-referenced from the creator's own profiles.</p>
      <p>We calculate additional metrics (such as engagement rates, estimated impressions, and watch hours) based on this publicly available data. These calculated metrics are estimates generated by Streamers Connected and may differ from official platform analytics.</p>

      <h3>Information from Creator-Authorised Integrations (TikTok OAuth)</h3>
      <p>
        Where a creator chooses to connect their TikTok account to our Service via TikTok's OAuth flow, we access data covered by the permission scopes the creator explicitly grants. This may include:
      </p>
      <ul>
        <li><strong>Profile information:</strong> username, display name, profile image, follower count, following count, and account verification status</li>
        <li><strong>Video metadata:</strong> titles, descriptions, thumbnails, and publish dates for videos the creator submits as campaign deliverables</li>
        <li><strong>Video performance metrics:</strong> number of views, number of likes, number of comments, number of shares, watch time, and engagement rate</li>
        <li><strong>Audience demographics (aggregated only):</strong> aggregated and anonymised audience data such as country/region breakdown, gender split, and age range distribution, as provided by the TikTok Analytics API</li>
      </ul>
      <p>
        We do not access individual viewer identities, private messages, or any data outside the scopes the creator has consented to. Audience demographic data is only ever displayed in aggregated form and cannot be used to identify any individual viewer.
      </p>
      <p>
        We use this creator-authorised data solely to generate campaign performance reports for the brands the creator has agreed to work with. Creators may revoke our access at any time via their TikTok account settings, which will immediately stop further data collection.
      </p>

      <h3>Information from Creator-Authorised Integrations (Instagram)</h3>
      <p>
        Where a creator chooses to connect their Instagram Business or Creator account to our Service via Meta's authorisation flow, we access data covered by the permission scopes the creator explicitly grants. This may include:
      </p>
      <ul>
        <li><strong>Profile information:</strong> username, display name, profile image, and follower count</li>
        <li><strong>Media metadata:</strong> captions, thumbnails, media type, and publish dates for posts and reels the creator submits as campaign deliverables</li>
        <li><strong>Media performance metrics:</strong> number of likes, number of comments, and video views, and — where authorised — reach and saves</li>
      </ul>
      <p>
        We do not access individual viewer identities, private messages, or any data outside the scopes the creator has consented to. We use this creator-authorised data solely to generate campaign performance reports for the brands the creator has agreed to work with. Creators may revoke our access at any time via their Instagram or Facebook account settings, which will immediately stop further data collection.
      </p>

      <p>
        <strong>Important:</strong> Aside from the creator-authorised integrations described above, we only access, collect, and display publicly available information. We do not access private user data, authentication tokens, or any information that requires user authorisation from YouTube, Twitch, Kick, TikTok, or Instagram beyond the OAuth scopes a creator has explicitly granted.
      </p>

      <h2>3. How We Use Your Information</h2>
      <p>We use the information we collect to:</p>
      <ul>
        <li>Provide and maintain our Service</li>
        <li>Create and manage client dashboard access</li>
        <li>Display creator analytics and campaign performance data</li>
        <li>Communicate with you about your account or our services</li>
        <li>Improve and optimise our platform</li>
        <li>Comply with legal obligations</li>
      </ul>

      <h2>4. YouTube API Services</h2>
      <p>Our Service uses YouTube API Services to retrieve publicly available creator data. By using our Service:</p>
      <ul>
        <li>You agree to be bound by the <a href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer">YouTube Terms of Service</a></li>
        <li>You acknowledge that Google's Privacy Policy applies to the YouTube data we display: <a href="http://www.google.com/policies/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a></li>
      </ul>
      <p>
        <strong>Revoking Access:</strong> You can revoke our access to YouTube data via the Google security settings page at{' '}
        <a href="https://security.google.com/settings/security/permissions" target="_blank" rel="noopener noreferrer">https://security.google.com/settings/security/permissions</a>.
      </p>

      <h2>5. TikTok API Services</h2>
      <p>Our Service uses TikTok API Services to retrieve creator data, including data accessed via TikTok's OAuth flow where a creator has explicitly granted permission. By using our Service:</p>
      <ul>
        <li>You agree to be bound by the <a href="https://www.tiktok.com/legal/page/global/terms-of-service/en" target="_blank" rel="noopener noreferrer">TikTok Terms of Service</a></li>
        <li>You acknowledge that the <a href="https://www.tiktok.com/legal/page/global/privacy-policy/en" target="_blank" rel="noopener noreferrer">TikTok Privacy Policy</a> applies to the TikTok data we display</li>
      </ul>
      <p>
        <strong>How we use TikTok data:</strong> Creator-authorised TikTok data is used solely to generate campaign performance reports for the brands a creator has agreed to work with. We do not sell, share, or otherwise disclose creator data to third parties outside the scope of those campaigns, and we do not use it for advertising or to train machine-learning models.
      </p>
      <p>
        <strong>Revoking Access:</strong> Creators can revoke our access to their TikTok data at any time by visiting{' '}
        <a href="https://www.tiktok.com/setting" target="_blank" rel="noopener noreferrer">TikTok account settings</a>{' '}
        and removing Streamers Connected from connected apps, or by contacting us directly. Revocation takes effect immediately and stops further data collection. Data already collected for active campaigns will be retained in line with the retention policy described below, unless the creator requests deletion.
      </p>

      <h2>6. Instagram Graph API (Meta Platforms)</h2>
      <p>Our Service uses the Instagram Graph API, provided by Meta Platforms, to retrieve publicly available creator data and — where a creator has explicitly granted permission — data accessed via Meta's authorisation flow. By using our Service:</p>
      <ul>
        <li>You agree to be bound by the <a href="https://help.instagram.com/581066165581870" target="_blank" rel="noopener noreferrer">Instagram Terms of Use</a> and the <a href="https://developers.facebook.com/terms/" target="_blank" rel="noopener noreferrer">Meta Platform Terms</a></li>
        <li>You acknowledge that the <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer">Meta Privacy Policy</a> applies to the Instagram data we display</li>
      </ul>
      <p>
        <strong>How we use Instagram data:</strong> Instagram data is used solely to identify and measure campaign content and to generate campaign performance reports for the brands a creator has agreed to work with. We do not sell, share, or otherwise disclose creator data to third parties outside the scope of those campaigns, and we do not use it for advertising or to train machine-learning models.
      </p>
      <p>
        <strong>Revoking access and data deletion:</strong> Creators can revoke our access to any authorised Instagram data at any time via their Instagram or Facebook account settings, or by contacting us directly. Revocation takes effect immediately and stops further data collection. To request deletion of Instagram data we hold, contact us at <a href="mailto:info@streamersconnected.com">info@streamersconnected.com</a>; we will delete the relevant data in line with the retention policy described below.
      </p>

      <h2>7. Estimated and Calculated Metrics</h2>
      <p>
        Certain metrics displayed on our platform are calculated by Streamers Connected and are not provided directly by YouTube or other platform APIs. These include:
      </p>
      <ul>
        <li><strong>Engagement Rate:</strong> Calculated as (Likes + Comments) / Views</li>
        <li><strong>Estimated Impressions:</strong> Estimated based on publicly available data</li>
        <li><strong>Watch Hours:</strong> Estimated based on video duration and view counts</li>
        <li><strong>Average Views:</strong> Calculated from total views across content</li>
      </ul>
      <p>
        These metrics are labelled as "SC Est." or "Estimated" throughout the platform to distinguish them from native platform data. They are provided for informational purposes only and may differ from official analytics available to content creators.
      </p>

      <h2>8. Data Retention</h2>
      <p>We retain data according to the following policies:</p>
      <ul>
        <li><strong>YouTube metrics history:</strong> Historical performance data (snapshots of views, likes, comments over time) is retained for a rolling 30-day period and automatically refreshed or deleted thereafter.</li>
        <li><strong>TikTok metrics history:</strong> Performance data collected via the TikTok API (including creator-authorised OAuth data) is refreshed on a rolling 30-day basis. Snapshots older than 30 days are automatically overwritten or deleted, meaning we do not retain a long-term archive of TikTok performance data outside of this rolling window.</li>
        <li><strong>TikTok audience demographics:</strong> Aggregated demographic snapshots are also refreshed on the same 30-day rolling basis.</li>
        <li><strong>Instagram metrics history:</strong> Performance data retrieved via the Instagram Graph API (including any creator-authorised data) is refreshed on a rolling 30-day basis. Snapshots older than 30 days are automatically overwritten or deleted.</li>
        <li><strong>OAuth tokens:</strong> TikTok and Meta/Instagram access and refresh tokens are stored securely and deleted immediately if a creator revokes access, disconnects their account, or requests deletion.</li>
        <li><strong>Current metrics:</strong> The most recent metrics for content are retained and refreshed regularly.</li>
        <li><strong>Live stream data:</strong> Granular live stream tracking data is retained for 7 days. Summary statistics (average and peak concurrent viewers) may be retained longer.</li>
        <li><strong>Campaign summaries:</strong> Aggregated campaign performance summaries may be retained for business records purposes.</li>
        <li><strong>Personal information:</strong> Client account information is retained for as long as necessary to provide our services.</li>
      </ul>

      <h2>9. Cookies and Tracking Technologies</h2>
      <p>We use cookies and similar tracking technologies to collect and store information when you access our Service.</p>
      <p><strong>What we use:</strong></p>
      <ul>
        <li><strong>Essential cookies:</strong> Required for the platform to function, including session management and authentication</li>
        <li><strong>Analytics cookies:</strong> Help us understand how visitors interact with our platform</li>
      </ul>
      <p><strong>Third-party cookies:</strong> We may use third-party services (such as analytics providers) that place cookies on your device.</p>
      <p>
        You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our Service.
      </p>

      <h2>10. How We Share Your Information</h2>
      <p>We do not sell your personal information. We may share your information in the following circumstances:</p>

      <h3>With Service Providers</h3>
      <p>We may share information with third-party vendors who perform services on our behalf, such as:</p>
      <ul>
        <li>Hosting and server providers</li>
        <li>Analytics providers</li>
        <li>Email service providers</li>
      </ul>

      <h3>For Legal Purposes</h3>
      <p>We may disclose information if required to do so by law or in response to valid requests by public authorities.</p>

      <h3>Business Transfers</h3>
      <p>If we are involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.</p>

      <h2>11. Data Security</h2>
      <p>
        We implement appropriate technical and organisational measures to protect your personal information against unauthorised access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure.
      </p>

      <h2>12. Your Rights</h2>
      <p>Under UK data protection law, you have the right to:</p>
      <ul>
        <li>Access the personal information we hold about you</li>
        <li>Request correction of inaccurate information</li>
        <li>Request deletion of your information</li>
        <li>Object to or restrict processing of your information</li>
        <li>Request data portability</li>
      </ul>
      <p>To exercise any of these rights, please contact us using the details below.</p>

      <h2>13. Children's Privacy</h2>
      <p>
        Our Service is not directed to individuals under the age of 18. We do not knowingly collect personal information from children.
      </p>

      <h2>14. Changes to This Privacy Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
      </p>

      <h2>15. Contact Us</h2>
      <p>If you have any questions about this Privacy Policy or our data practices, please contact us:</p>
      <p>
        <strong>Streamers Connected Media Ltd</strong><br />
        58 Croydon Road<br />
        Caterham, CR3 6QB<br />
        United Kingdom
      </p>
      <p>Email: <a href="mailto:info@streamersconnected.com">info@streamersconnected.com</a></p>
    </div>
  );
};

export default PrivacyPolicy;
